import * as d3 from "d3";
import {
  drawRoleGroups,
  drawThreadGroups,
  drawInteractionLines,
  drawStateLines,
  drawLoopLines,
  drawMergeLines,
  drawInteractionGroups,
  drawActionGroups,
  drawTriggerGroups,
  drawStartRoleGroups,
  drawEllipsisGroups,
  drawCaseRefinementGroups,
  drawCaseConditionGroups,
  drawPartRefinementGroups,
  drawPartRepeatGroups,
  drawPartThreadGroups,
  drawPartRepeatThreadGroups,
} from "./RADDrawUtils.js";

/**
 * Creates the SVG container.
 */
const createSvg = (
  layoutRef,
  viewBoxWidth,
  viewBoxHeight,
  onBackgroundClick
) => {
  if (layoutRef.current && !layoutRef.current.querySelector("svg")) {
    const diagramSvg = d3
      .select(layoutRef.current)
      .append("svg")
      .attr("id", "rad-layout-svg")
      .attr("viewBox", `0 0 ${viewBoxWidth} ${viewBoxHeight}`)
      .on("click", onBackgroundClick)
      .node();

    d3.select(diagramSvg)
      .append("g")
      .attr("id", "rad-layout")
      .attr("cursor", "grab")
      .attr("pointer-events", "all");
  }
};

/**
 * Draws a thread and its children recursively.
 * Note that the rad object is the computed position rad, not the state rad.
 */
const drawThread = (
  threadGroup,
  thread,
  rad,
  dragBehavior,
  onClick,
  radFunctions
) => {
  const {
    getThreadSequence,
    getThreadCaseRefinements,
    getThreadPartRefinements,
    getThreadPartRepeats,
    getSubthreads,
    addPrecedingPositions,
    getFarY,
    getActivityByPrestate,
    getActivityByPoststate,
  } = radFunctions;
  const farY = getFarY(rad)(thread);
  const threadStatus = thread.status || "main";
  const threadShowLoop = thread.showLoop !== undefined ? thread.showLoop : true;
  const threadPoststate = thread.poststate_id || null;
  const states = rad.states || [];

  const activities = getThreadSequence(rad)(
    thread.id,
    thread.prestate_id
  ).filter((activity) => activity.type !== "void");

  drawStateLines(
    threadGroup,
    threadStatus,
    threadShowLoop,
    threadPoststate,
    farY,
    activities,
    states,
    dragBehavior(),
    onClick
  );

  // Draw loop lines if applicable
  if (threadStatus === "looped" && threadShowLoop) {
    const loopFromActivity =
      activities.length > 0 ? activities[activities.length - 1] : null;
    // get the activity whose prestate matches the thread's poststate
    const loopToActivity = getActivityByPrestate(rad)(threadPoststate);
    const priorActivity = getActivityByPoststate(rad)(
      loopToActivity.prestate_id
    );
    const threadNudgeY =
      !loopFromActivity || priorActivity.thread_id === thread.id ? 0 : 20;

    const yOffset = priorActivity
      ? (loopToActivity.position.y - priorActivity.position.y) / 2 -
        threadNudgeY
      : 20;

    if (!loopToActivity) return;

    const loopFromPosition = loopFromActivity
      ? loopFromActivity.absPos
      : thread.absPos;

    const loopToPosition = {
      x: loopToActivity.absPos.x,
      y: loopToActivity.absPos.y - yOffset,
    };

    // if looping back to start of the thread, nudge down a bit. Bit kludgy but looks better.
    const yNudge = loopToActivity.id === activities[0]?.id ? 20 : 0;

    const dx = loopFromPosition.x - loopToPosition.x;
    const dy = loopFromPosition.y - loopToPosition.y - yNudge;
    const separation = { dx, dy };

    if (loopToPosition) {
      drawLoopLines(
        threadGroup,
        thread,
        loopFromActivity,
        loopToActivity,
        separation,
        onClick
      );
    }
  }

  // Draw merge lines if applicable
  if (threadStatus === "merged") {
    // note that looped and merged are disjoint on subthreads and threads respectively
    const terminalActivity =
      activities.length > 0 ? activities[activities.length - 1] : null; // Threads are never empty so this should not happen
    const mergeToActivity = getActivityByPrestate(rad)(threadPoststate);
    // We want the merge line to hit the state marker so get the prior activity's position
    // as well and get the midpoint between them. Position are relative offsets to the
    // thread
    const priorActivity = getActivityByPoststate(rad)(
      mergeToActivity.prestate_id
    );
    const yOffset = priorActivity
      ? (mergeToActivity.position.y - priorActivity.position.y) / 2
      : 25;

    if (!mergeToActivity) return;
    const mergeFromPosition = terminalActivity
      ? terminalActivity.absPos
      : thread.absPos;
    const mergeToPosition = {
      x: mergeToActivity.absPos.x,
      y: mergeToActivity.absPos.y - yOffset,
    };
    const dx = mergeFromPosition.x - mergeToPosition.x;
    const dy = mergeFromPosition.y - mergeToPosition.y;
    const separation = { dx, dy };
    if (mergeToPosition) {
      drawMergeLines(
        threadGroup,
        thread,
        terminalActivity,
        separation,
        onClick
      );
    }
  } else if (threadStatus !== "merged" && thread.class === "thread") {
    // This will clear extant merge threads from the diagram.
    drawMergeLines(threadGroup, thread, null, null, onClick);
  } else {
    // Subthreads cannot merge.
  }

  const elementsToDraw = [
    { type: "interaction", drawFn: drawInteractionGroups },
    { type: "action", drawFn: drawActionGroups },
    { type: "trigger", drawFn: drawTriggerGroups },
    { type: "start-role", drawFn: drawStartRoleGroups },
    { type: "ellipsis", drawFn: drawEllipsisGroups },
    { type: "repeat", drawFn: drawPartRepeatGroups },
  ];

  elementsToDraw.forEach(({ type, drawFn }) => {
    const nodes = activities.filter(
      (activity) => activity.type === type && activity.thread_id === thread.id
    );
    if (nodes.length === 0) return;
    drawFn(threadGroup, nodes, dragBehavior(), onClick);
  });

  const caseRefs = getThreadCaseRefinements(rad)(thread.id);
  if (caseRefs.length > 0) {
    drawCaseRefinementGroups(threadGroup, caseRefs, dragBehavior(), onClick);
    caseRefs.forEach((caseRef) => {
      const caseGroup = threadGroup.select(
        `g.case-refinement-group[data-id="${caseRef.id}"]`
      );
      const caseConditions = addPrecedingPositions(
        getSubthreads(rad)(caseRef.id)
      );
      drawCaseConditionGroups(
        caseGroup,
        caseConditions,
        dragBehavior(),
        onClick
      );
      caseConditions.forEach((caseCondition) => {
        const caseConditionGroup = caseGroup.select(
          `g.subthread-group[data-id="${caseCondition.id}"]`
        );
        drawThread(
          caseConditionGroup,
          caseCondition,
          rad,
          dragBehavior,
          onClick,
          radFunctions
        );
      });
    });
  }

  const partRefs = getThreadPartRefinements(rad)(thread.id);
  if (partRefs.length > 0) {
    drawPartRefinementGroups(threadGroup, partRefs, dragBehavior(), onClick);
    partRefs.forEach((partRef) => {
      const partGroup = threadGroup.select(
        `g.part-refinement-group[data-id="${partRef.id}"]`
      );
      const partThreads = addPrecedingPositions(getSubthreads(rad)(partRef.id));
      drawPartThreadGroups(partGroup, partThreads, dragBehavior(), onClick);
      partThreads.forEach((partThread) => {
        const partThreadGroup = partGroup.select(
          `g.subthread-group[data-id="${partThread.id}"]`
        );
        drawThread(
          partThreadGroup,
          partThread,
          rad,
          dragBehavior,
          onClick,
          radFunctions
        );
      });
    });
  }

  const partReps = getThreadPartRepeats(rad)(thread.id);
  partReps.forEach((partRep) => {
    const partGroup = threadGroup.select(
      `g.part-repeat-group[data-id="${partRep.id}"]`
    );
    const partThreads = getSubthreads(rad)(partRep.id);
    drawPartRepeatThreadGroups(partGroup, partThreads, dragBehavior(), onClick);
    partThreads.forEach((partThread) => {
      const partThreadGroup = partGroup.select(
        `g.subthread-group[data-id="${partThread.id}"]`
      );
      drawThread(
        partThreadGroup,
        partThread,
        rad,
        dragBehavior,
        onClick,
        radFunctions
      );
    });
  });
};

/**
 * Draws the full RAD layout.
 */
const drawRADLayout = (layoutRef, rad, dragBehavior, onClick, radFunctions) => {
  const svg = d3.select(layoutRef).select("svg");
  const g = svg.select("#rad-layout");
  const { getRoleThreads, getConnectedInteractions } = radFunctions;

  const { roles = [] } = rad;

  drawRoleGroups(g, roles, dragBehavior(), onClick);

  roles.forEach((role) => {
    const roleThreads = getRoleThreads(rad)(role.id);
    const roleGroup = g.select(`g.role-group[data-id="${role.id}"]`);
    drawThreadGroups(roleGroup, roleThreads, dragBehavior(), onClick);

    roleThreads.forEach((thread) => {
      const threadGroup = roleGroup.select(
        `g.thread-group[data-id="${thread.id}"]`
      );
      drawThread(threadGroup, thread, rad, dragBehavior, onClick, radFunctions);
    });
  });

  const connections = getConnectedInteractions(rad);
  drawInteractionLines(g, connections, dragBehavior(), onClick);
};

export { createSvg, drawRADLayout };
